'use client';

/**
 * ReferralStatsSection - Two stat cards matching target design exactly
 * Transparent backgrounds, subtle borders, precise spacing
 */

import React from 'react';
import { Users, Wallet, Crown } from 'lucide-react';
import type { ReferralStats } from '../types';
import { formatPrice } from '@/lib/utils/format-price';

interface ReferralStatsSectionProps {
  stats: ReferralStats;
}

export function ReferralStatsSection({ stats }: ReferralStatsSectionProps) {
  const nextTierTarget = stats.nextTier?.min_referrals ?? stats.payingActiveReferrals;
  const progressPercent = stats.progressToNextTier;

  // When a non-"all" time range is active, the earnings card rescopes to that
  // window (Σ ledger amounts in period × current rate) and keeps lifetime as a
  // secondary line — the "go back in time" view Albert asked for.
  const isPeriodScoped = stats.period.key !== 'all';
  const earningsLabel = isPeriodScoped ? `${stats.period.label} Earnings` : 'Lifetime Earnings';
  const earningsValue = isPeriodScoped ? stats.period.earnings : stats.lifetimeEarnings;

  return (
    <div className="flex flex-col lg:flex-row gap-4">
      {/* Left Card: Referrals Progress - 60% width - Bubble Style */}
      <div className="flex-[3] rounded-[2rem] bg-white/60 dark:bg-zinc-900/40 backdrop-blur-xl border border-zinc-200 dark:border-zinc-800 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 rounded-lg bg-amber-500/10 flex items-center justify-center">
              <Users className="h-3.5 w-3.5 text-amber-500" />
            </div>
            <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-medium">
              Your Progress
            </span>
          </div>
          {/* Tier badge */}
          <span className="text-[10px] px-3 py-1 rounded-lg bg-amber-500/10 text-amber-400 font-semibold">
            {stats.currentTier.display_name.split(' ')[0]}
          </span>
        </div>

        {/* Value */}
        <div className="flex items-baseline gap-2 mb-2">
          <span className="text-4xl font-black tracking-tighter text-zinc-900 dark:text-white tabular-nums">
            {stats.payingActiveReferrals}
          </span>
          <span className="text-4xl font-black tracking-tighter text-zinc-300 dark:text-zinc-700 tabular-nums">
            / {nextTierTarget}
          </span>
          <span className="text-sm text-zinc-500 ml-2">Referrals</span>
        </div>

        {/* Description */}
        <p className="text-xs text-zinc-500 dark:text-zinc-600 mb-6">
          {stats.referralsToNextLevel > 0
            ? `${stats.referralsToNextLevel} more to ${stats.nextTier?.display_name.split(' ')[0] || 'next tier'}`
            : 'Max tier reached!'}
        </p>

        {/* Progress Bar */}
        <div className="h-1 bg-zinc-200 dark:bg-zinc-800 rounded-full overflow-hidden mb-4">
          <div
            className="h-full bg-amber-500 rounded-full transition-all duration-500"
            style={{ width: `${Math.min(progressPercent, 100)}%` }}
          />
        </div>

        {/* Progress label */}
        <div className="flex justify-end">
          <span className="text-[10px] px-3 py-1 rounded-lg bg-amber-500/10 text-amber-400 font-semibold">
            {Math.round(progressPercent)}% Progress
          </span>
        </div>
      </div>

      {/* Right Card: Earnings - 40% width - Bubble Style */}
      <div className="flex-[2] rounded-[2rem] bg-white/60 dark:bg-zinc-900/40 backdrop-blur-xl border border-zinc-200 dark:border-zinc-800 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 rounded-lg bg-emerald-500/10 flex items-center justify-center">
              <Wallet className="h-3.5 w-3.5 text-emerald-500" />
            </div>
            <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-medium">
              {earningsLabel}
            </span>
          </div>
          {/* Commission badge */}
          <div className="flex items-center gap-1.5">
            <Crown className="h-4 w-4 text-amber-500" />
            <span className="text-sm font-black text-amber-500">
              {stats.currentTier.commission_percent}%
            </span>
          </div>
        </div>

        {/* Value */}
        <span className="text-4xl font-black tracking-tighter text-zinc-900 dark:text-white tabular-nums block mb-2">
          {formatPrice(earningsValue, 'EUR', { minimumFractionDigits: 2 })}
        </span>

        {/* Description */}
        {isPeriodScoped ? (
          <div className="space-y-0.5">
            <p className="text-xs text-zinc-500 dark:text-zinc-600">
              {stats.period.newClients} new {stats.period.newClients === 1 ? 'client' : 'clients'}{' '}
              this period
            </p>
            <p className="text-[11px] text-zinc-400 dark:text-zinc-700 tabular-nums">
              Lifetime: {formatPrice(stats.lifetimeEarnings, 'EUR', { minimumFractionDigits: 2 })}
            </p>
            {stats.period.isPartial && (
              <p className="text-[11px] text-amber-500/80">Limited ledger history for this range</p>
            )}
          </div>
        ) : (
          <p className="text-xs text-zinc-500 dark:text-zinc-600">
            {stats.totalReferrals} referred {stats.totalReferrals === 1 ? 'client' : 'clients'}
          </p>
        )}
      </div>
    </div>
  );
}
